import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { echo } from '@laravel/echo-react';
import { mergeAssetPayloadIntoSingleCache, mergeVariantsIntoSingleCache } from '@/hooks/websocket/mergeAssetPayload';
import type { AssetUpdatedPayload, AssetVersionActivatedPayload } from '@/hooks/websocket/types';

interface UseAssetChannelOptions {
  enabled?: boolean;
}

export const useAssetChannel = (assetId: string | undefined, options: UseAssetChannelOptions = {}): void => {
  const { enabled = true } = options;
  const queryClient = useQueryClient();

  const handleAssetUpdated = useCallback(
    (event: AssetUpdatedPayload) => {
      mergeAssetPayloadIntoSingleCache(queryClient, event);
    },
    [queryClient],
  );

  const handleVersionActivated = useCallback(
    (event: AssetVersionActivatedPayload) => {
      mergeVariantsIntoSingleCache(queryClient, event);
    },
    [queryClient],
  );

  useEffect(() => {
    if (!assetId || !enabled) {
      return;
    }

    const channelName = `asset.${assetId}`;
    const channel = echo().private(channelName);
    channel.listen('.asset.updated', handleAssetUpdated);
    channel.listen('.asset.version.activated', handleVersionActivated);

    return () => {
      channel.stopListening('.asset.updated', handleAssetUpdated);
      channel.stopListening('.asset.version.activated', handleVersionActivated);
      echo().leaveChannel(`private-${channelName}`);
    };
  }, [assetId, enabled, handleAssetUpdated, handleVersionActivated]);
};
